import { useEffect, useState } from 'react';
import Modal from './Modal';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { executeSophiaAction, selectSelectedProducts } from '../features/thread/threadSlice';
import { normaliseProductDetails, ProductDetails } from '../utils/product';

interface StateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function StateModal({ isOpen, onClose }: StateModalProps) {
  const dispatch = useAppDispatch();
  const selectedSkus = useAppSelector(selectSelectedProducts);
  const [products, setProducts] = useState<ProductDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setProducts([]);
      setError(null);
      return;
    }
    async function load() {
      try {
        setLoading(true);
        const result: ProductDetails[] = [];
        const payload = await dispatch(
          executeSophiaAction({
            actionName: 'global-get-sku-details',
            params: { 'global-skus-details': selectedSkus },
            refreshThread: false,
          }),
        ).unwrap();
        const response = payload.data as { 'global-sku-details'?: Record<string, unknown> };
        const details = response?.['global-sku-details'] || {};
        result.push(...Object.values(details).map(detail => normaliseProductDetails(detail)));
        setProducts(result);
        setError(null);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [dispatch, isOpen, selectedSkus]);

  const handleRemove = async (sku: string) => {
    await dispatch(
      executeSophiaAction({
        actionName: 'global-unset-select-product',
        params: { 'global-select-product': sku },
        refreshThread: true,
      }),
    );
  };

  const handleClearAll = async () => {
    await dispatch(
    executeSophiaAction({
        actionName: 'global-clear-all-products',
        refreshThread: true,
    }),
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex max-w-[95vw] flex-col items-center overflow-hidden bg-white p-2" style={{ width: '960px' }}>
        <div className="sticky top-0 flex w-full items-center justify-between gap-2 bg-white p-2">
          <div className="flex flex-row items-center justify-start">
            <button type="button" onClick={onClose} className="border-r-2 p-2 pr-4 pb-3 text-2xl">×</button>
            <h2 className="pl-3 text-2xl font-bold">Prodotti Selezionati ({products.length})</h2>
          </div>
          <div className="flex flex-row justify-end">
            <button
              type="button"
              onClick={handleClearAll}
              className="px-3 py-2 text-xs font-semibold bg-white text-black border-2 border-black h-10"
            >
              Deseleziona tutti
            </button>
          </div>
        </div>
        <div className="h-px w-full bg-gray-200" />
        {loading && <p className="p-4 text-sm text-gray-500">Loading…</p>}
        {error && <p className="p-4 text-sm text-red-500">{error}</p>}
        {!loading && !error && (
          <div className="flex w-full max-w-[95vw] flex-col items-center gap-3 bg-white p-2">
            {products.length === 0 ? (
              <p className="text-gray-500">No products selected.</p>
            ) : (
              <div className="flex w-full flex-col gap-3">
                <div className="flex flex-col gap-3 mt-4 w-full items-center">
                  {products.map((product, index) => (
                    <div
                      key={product.sku}
                      className={`flex h-24 w-full flex-row flex-nowrap items-center justify-center gap-2 px-3 py-2 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-[#F3F3F3]'
                      }`}
                    >
                      <div className="flex flex-row flex-nowrap items-center gap-2">
                        {product.images[0] && (
                          <div className="h-20 w-20 flex-none overflow-hidden rounded-lg">
                            <img
                              src={product.images[0]}
                              alt={product.code}
                              className="h-full w-full object-cover"
                            />
                          </div>
                        )}
                        <span className="flex-1 truncate text-sm font-semibold text-gray-800">{product.code}</span>
                        <button
                          type="button"
                          onClick={() => handleRemove(product.sku)}
                          className="ml-2 px-3 py-2 text-xs font-semibold bg-white text-black border-2 border-black h-10"
                        >
                          <i className="ti-trash text-base font-bold" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default StateModal;
